import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'utils/api';

// What pnet-api knows about texting a number (GET /comms/phone-health):
// whether a text would be refused and why, plus the Twilio Lookup line type.
export type PhoneTextability = {
  blocked: boolean;
  warn: boolean;
  // "Mobile", "Landline", "VoIP landline", … — null when unclassified.
  lineTypeLabel: string | null;
  // Why a text is refused — null unless blocked.
  reason: string | null;
};

type PhoneHealthEntry = {
  phone: string;
  warn: boolean;
  blocked: boolean;
  health: {
    lineType: string | null;
    lineTypeCarrier: string | null;
    optedOutAt: string | null;
  } | null;
};

const LINE_TYPE_LABELS: Record<string, string> = {
  mobile: 'Mobile',
  landline: 'Landline',
  fixedVoip: 'VoIP landline',
  nonFixedVoip: 'VoIP',
  tollFree: 'Toll-free',
  voicemail: 'Voicemail',
  pager: 'Pager'
};

// The batch endpoint takes at most this many numbers per request.
const BATCH_LIMIT = 25;
const DEBOUNCE_MS = 350;

// Last 10 digits — the key both modals and the server agree on regardless of
// how the number was typed.
export function phoneKey(phone: string): string {
  return phone.replace(/\D/g, '').slice(-10);
}

export function isCompletePhone(phone: string): boolean {
  return phoneKey(phone).length === 10;
}

export function describeTextability(entry: PhoneHealthEntry): PhoneTextability {
  const lineType = entry.health?.lineType ?? null;
  const lineTypeLabel = lineType ? LINE_TYPE_LABELS[lineType] ?? null : null;
  if (!entry.blocked) {
    return { blocked: false, warn: entry.warn, lineTypeLabel, reason: null };
  }
  const reason = entry.health?.optedOutAt
    ? 'Opted out of texts (replied STOP) — call or email instead.'
    : `${
        lineTypeLabel ?? 'Landline'
      } — can't receive texts. Call or email instead.`;
  return { blocked: true, warn: true, lineTypeLabel, reason };
}

/**
 * Textability of the numbers a modal lists plus the one being typed, so a
 * landline or opted-out customer can be marked (and the send disabled) before
 * the user tries. Answers are fetched once per distinct number while
 * `enabled`; the server caches each number's Twilio classification for 90
 * days, so this costs a lookup only for numbers nobody has texted. Any
 * failure leaves a number unknown — never blocked.
 */
export function usePhoneTextability({
  phones,
  enabled
}: {
  phones: string[];
  enabled: boolean;
}): {
  // Answers so far, keyed by phoneKey(); a stable object until an answer
  // arrives, so it can be a memo dependency.
  byKey: Record<string, PhoneTextability>;
  textabilityOf: (phone: string) => PhoneTextability | null;
} {
  const [byKey, setByKey] = useState<Record<string, PhoneTextability>>({});
  const requested = useRef<Set<string>>(new Set());

  const pending = useMemo(() => {
    const keys = new Map<string, string>();
    for (const phone of phones) {
      if (isCompletePhone(phone)) {
        keys.set(phoneKey(phone), phone);
      }
    }
    return Array.from(keys.entries());
  }, [phones]);
  const pendingSignature = pending.map(([key]) => key).join(',');

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const toFetch = pending.filter(([key]) => !requested.current.has(key));
    if (!toFetch.length) {
      return undefined;
    }
    const fetchChunk = async (chunk: [string, string][]) => {
      try {
        const response = await axios.get<{ results: PhoneHealthEntry[] }>(
          `${process.env.REACT_APP_PARTNERNET_URL}/api/pnet/comms/phone-health/batch`,
          { params: { phones: chunk.map(([, phone]) => phone).join(',') } }
        );
        const update: Record<string, PhoneTextability> = {};
        for (const entry of response.data?.results ?? []) {
          update[phoneKey(entry.phone)] = describeTextability(entry);
        }
        setByKey((previous) => ({ ...previous, ...update }));
      } catch {
        // Unknown stays unknown: the server still refuses a landline at
        // send time, and the modal shows that reason then.
        chunk.forEach(([key]) => requested.current.delete(key));
      }
    };
    const timer = setTimeout(() => {
      toFetch.forEach(([key]) => requested.current.add(key));
      const chunks: [string, string][][] = [];
      for (let start = 0; start < toFetch.length; start += BATCH_LIMIT) {
        chunks.push(toFetch.slice(start, start + BATCH_LIMIT));
      }
      // Each chunk handles its own errors (see fetchChunk).
      Promise.all(chunks.map(fetchChunk)).catch(() => undefined);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // pendingSignature captures every phone in `pending`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pendingSignature]);

  return {
    byKey,
    textabilityOf: (phone: string) =>
      isCompletePhone(phone) ? byKey[phoneKey(phone)] ?? null : null
  };
}
